// Rotary drilling loop (M2_SPEC §1, §5, §6; DESIGN §5.2, §14). drilling.js dispatches every DRILLING day of a
// well with well.rigType === 'rotary' here; cable-tool wells keep the M1 loop untouched.
//
//   rop = baseRop × power × bit.speed × bitCondF × hardF × wobF × rpmF × mudF × hydF × ballF × crew × tour × rigCond
//   hardF  = 2/(1+h) (M1-tuned form of 1/h), × 0.2 above the bit's maxHard
//   rpmF   = rpm ^ (h ≥ 6 ? 0.3 : 0.6)      mudF = mud.ropMult × (1 − 0.05 × overbalance) × deepRopMult
//   hydF   = min(1, pump.ratedDepth/depth)^0.3 × hydraulicsMult (no pump: × 0.25)
//   ballF  = gumbo: 1 − 0.5 × max(0, wob − 1.1) × bit.balling × (1 − cake)
//   teeth  wear/ft = h/(life × lifeMults) × 100 × wob^1.8 × rpm^1.3 × (h > maxHard ? 3 : 1)
//   bearing wear/day (cone bits) = 100/bearingDays × rpm^1.5 → 0: 30%/day the cone locks (fishing)
import { chance, range, int, nextFloat } from '../core/rng.js';
import { ROTARY, MUD, CONTRACT_DRILLING, contractPerFt, contractMaxDepth } from '../data/m2/rules.js';
import { eqDef, fieldDef, rigStats, bitStats, stat, eraDollars, unitPrice, defsByCat, findDef, yf, isAvailable } from './defs.js';
import { strataAt, stratumAt, reservoirsAt } from './geo.js';
import { startTimer, cancelTimer } from './timers.js';
import { activeRigCrew, traitCount } from './crew.js';
import { effects } from './research.js';
import { stockQty, takeStock } from '../core/state.js';
import {
  wellRig, setTask, clearTask, crewFactors, enginePower, HARD_TUNE, setProblem, startFishing, startStraighten,
  reachTD, hitPay, bestSpareBit, startBitChange, autoCase, collapse, chargeFuel, createWell, WOB_MIN, WOB_MAX,
  hardFounderMult, touchToday, touchRopMult, touchWearMult, waitingOnDriller,
} from './drilling.js';
import { mudDef, mudWeight, cakeQuality, defaultMud, pressureRolls, pressureWindow, baritePerFt, learnPore, zoneProfile } from './mud.js';
import { ensureString, fatigueDay, rollTwistOff, loseJoints, jointsFor, pipeStats } from './pipe.js';
import { onKick } from './wellcontrol.js';
import { collapseRoll } from './cement.js';
import { applyAutomation, hasMudMan } from './automation.js';
import { clamp, notify, log, spend, fmt$ } from './util.js';
import { bump } from './m2util.js';
import { m3DrillGate, m3RopMult, m3TripMult, m3BitDamage } from './m3/offshore.js'; // M3 ops: weather/supply/ice gate, drilling tech
import { isLateralPhase, lateralDay, shaleRopMult } from './m4/shale.js'; // M4-A: curve & lateral drilling (wells with well.lateral, ≥ 2000)

export const RPM_MIN = ROTARY.rpmRange[0], RPM_MAX = ROTARY.rpmRange[1];
export const RPM_STOPS = { Slow: 0.7, Normal: 1.0, Fast: 1.3, 'Spin it': 1.6 };
export const LOST_CONE_PER_DAY = 0.3;
export const STUCK_WORK_CHANCE = 0.4;
export const STUCK_WORK_DAYS = 5;
export const FISHING_CONTRACTOR = 400;     // 1901 $ (DESIGN §5.3 "fishing contractor ($, 80%)")
export const SIDETRACK_LOSS_FT = 300;

export const isRotaryRig = (rig) => !!rig && rigStats(eqDef(rig.defId)).rigType === 'rotary';
export const isRotaryWell = (well) => well?.rigType === 'rotary';

// ---- drilling context: the rig (or the drilling contractor) on the well ----------------------------
const CONTRACT_CF = { crew: [], hasDriller: true, missing: 0, crewF: 1, roleF: 1, tourF: 1, traitF: 1 };

export function contractorRigDef(state) {
  const list = defsByCat('rig').filter((d) => stat(d, 'rigType', 'cable') === 'rotary' && d.year <= Math.max(1901, Math.floor(yf(state))));
  list.sort((a, b) => b.year - a.year);
  return list[0] ?? null;
}

function bestDef(state, cat, pred = () => true) {
  const list = defsByCat(cat).filter((d) => pred(d) && (d.year <= Math.max(1901, Math.floor(yf(state)))) && (!d.obsoleteYear || yf(state) < d.obsoleteYear));
  list.sort((a, b) => b.year - a.year);
  return list[0] ?? null;
}

export function drillCtx(state, well) {
  if (well.contract) {
    const rigDef = eqDef(well.contract.rigDefId) ?? contractorRigDef(state);
    const bitDef = eqDef(well.contract.bitDefId) ?? bestDef(state, 'bit', (d) => d.rigTypes?.includes('rotary'));
    return {
      contractor: true, rig: null, rigDef, rs: rigStats(rigDef), power: 1.6, cf: CONTRACT_CF,
      bit: { defId: bitDef?.id, condition: 85, maxCondition: 100, bearing: 100 }, bitDef,
      pumpDef: bestDef(state, 'rig_gear', (d) => stat(d, 'slot', null) === 'pump'), collarsDef: null,
      pipeDef: bestDef(state, 'pipe'), rigCond: 90, tours: 2,
    };
  }
  const rig = wellRig(state, well);
  if (!rig) return null;
  const rigDef = eqDef(rig.defId);
  const bit = state.items[rig.bitUid] ?? null;
  const gearDef = (slot) => { const uid = rig.gear?.[slot]; return uid && state.items[uid] ? eqDef(state.items[uid].defId) : null; };
  return {
    contractor: false, rig, rigDef, rs: rigStats(rigDef), power: enginePower(state, rig), cf: crewFactors(state, rig),
    bit, bitDef: bit ? eqDef(bit.defId) : null, pumpDef: gearDef('pump'), collarsDef: gearDef('collars'),
    pipeDef: well.string ? eqDef(well.string.defId) : null, rigCond: rig.condition, tours: rig.tours,
  };
}

// WOB factors with the collars' founder bonus (DESIGN §14).
export function rotaryWobFactors(state, ctx, wob, h) {
  const bs = bitStats(ctx.bitDef);
  const drillerSkill = ctx.cf.crew.find((c) => c.role === 'driller')?.skill ?? 50;
  const founder = (stat(ctx.bitDef, 'founderPoint', 1.25) + stat(ctx.collarsDef, 'founderBonus', 0)) * (1 + 0.1 * (drillerSkill - 50) / 50) * hardFounderMult(h, bs.maxHard);
  const effW = wob <= founder ? wob : founder + 0.3 * (wob - founder);
  const e = effects(state);
  return {
    founder,
    ropMult: Math.pow(effW, 0.9),
    wearMult: Math.pow(wob, 1.8 + e.wobWearExpDelta) * (h > bs.maxHard ? 1.5 : 1),
    riskMult: 1 + 2.5 * Math.max(0, wob - 1.1) * e.highWobRiskMult,
  };
}

export function hydraulicsF(ctx, depth) {
  if (!ctx.pumpDef) return ROTARY.noPumpRopMult;
  const rated = stat(ctx.pumpDef, 'ratedDepth', 3500);
  return Math.pow(Math.min(1, rated / Math.max(1, depth)), ROTARY.hydraulicsExp) * stat(ctx.pumpDef, 'hydraulicsMult', 1);
}

export function ballingF(s, ctx, wob, mdef) {
  if (!s.balling) return 1;
  return clamp(1 - ROTARY.ballingPenalty * Math.max(0, wob - ROTARY.ballingWobKnee) * stat(ctx.bitDef, 'balling', 1) * (1 - cakeQuality(mdef)), 0.2, 1);
}

export function mudRopF(well, s, mdef, mw) {
  if (!mdef) return 1;
  let f = stat(mdef, 'ropMult', 1) * (1 - 0.05 * Math.max(0, mw - (s.pore ?? 8.6)));
  const deep = stat(mdef, 'deepRopMult', null);
  if (deep && well.depth > deep[0]) f *= deep[1];
  return Math.max(0.1, f);
}

// Above the bit's maxHard (DESIGN §3.3: ROP ×0.2, wear ×3). Tuned: one point over (a fishtail in Spindletop's
// cavernous h5 cap rock) is a struggle, not a wall — ROP ×0.6, wear ×2; two or more points over: ×0.2 / ×3.
export const overHardRop = (h, maxHard) => (h <= maxHard ? 1 : h - maxHard < 2 ? 0.6 : 0.2);
export const overHardWear = (h, maxHard) => (h <= maxHard ? 1 : h - maxHard < 2 ? 2 : 3);

export const rpmExp = (h) => (h >= ROTARY.hardThreshold ? ROTARY.ropRpmExpHard : ROTARY.ropRpmExpSoft);

// Rate of penetration (ft/day) in stratum s. opts: { wob, rpm } overrides for projections.
export function rotaryRop(state, well, s, ctx = drillCtx(state, well), opts = {}) {
  if (!ctx || !ctx.bit || !ctx.bitDef) return 0;
  if (!ctx.cf.hasDriller || ctx.power <= 0) return 0;
  const wob = opts.wob ?? well.wob ?? 1;
  const rpm = opts.rpm ?? well.rpm ?? 1;
  const bs = bitStats(ctx.bitDef);
  const h = Math.max(1, s.hard);
  const hardF = (HARD_TUNE / (1 + h)) * overHardRop(h, bs.maxHard);
  const bitCondF = ctx.bit.condition <= 0 ? 0.1 : 0.3 + 0.7 * (ctx.bit.condition / 100);
  const wf = rotaryWobFactors(state, ctx, wob, h);
  const rpmF = Math.pow(rpm, rpmExp(h));
  const mdef = mudDef(well);
  const mw = mudWeight(well);
  const mudF = mudRopF(well, s, mdef, mw);
  const hydF = hydraulicsF(ctx, Math.max(well.depth, s.top));
  const ballF = ballingF(s, ctx, wob, mdef);
  const waterF = s.water && (s.pore ?? 9) > mw + 1e-9 ? 0.6 : 1;
  const rigCondF = 0.7 + 0.3 * ((ctx.rigCond ?? 100) / 100);
  const devF = (well.deviationDeg ?? 0) > 5 ? 0.8 : 1;
  const pipeF = ctx.pipeDef && well.depth > pipeStats(ctx.pipeDef).ratedDepth ? 0.5 : 1;
  const cf = ctx.cf;
  return ctx.rs.baseRop * ctx.power * bs.speed * bitCondF * hardF * wf.ropMult * rpmF * mudF * hydF * ballF
    * cf.crewF * cf.roleF * cf.tourF * cf.traitF * rigCondF * devF * pipeF * waterF * effects(state).ropMult
    * (yf(state) >= 1946 ? m3RopMult(state, well, ctx, h, rpm, rpmExp(h), overHardRop(h, bs.maxHard), mdef) : 1) // M3 ops: PDC, top drive, motors, MWD
    * (well.lateral ? shaleRopMult(state, well, ctx) : 1); // M4-A: batch drilling on a pad, missing M4 rig crew
}

export function toothWearPerFt(state, ctx, s, wob, rpm, oldHand = 1) {
  const bs = bitStats(ctx.bitDef);
  const h = Math.max(1, s.hard);
  const e = effects(state);
  const life = bs.life * (e.bitLifeMult ?? 1) * (e.rotaryBitLifeMult ?? 1);
  return h / life * 100 * overHardWear(h, bs.maxHard) * rotaryWobFactors(state, ctx, wob, h).wearMult * Math.pow(rpm, ROTARY.wearRpmExp) * oldHand;
}

export function bearingWearPerDay(ctx, rpm) {
  const days = stat(ctx.bitDef, 'bearingDays', null);
  return days ? (100 / days) * Math.pow(rpm, ROTARY.bearingRpmExp) : 0;
}

// Rotary trips take 2 × depth / rig.trip days (DESIGN §5.2).
export const rotaryTripDays = (rs, depth) => Math.max(1, (2 * depth) / Math.max(500, rs.trip || 4000));

// Rotary rigs and their wells: effective stableDays of open strata with mud in the hole (M2_SPEC §1.3).
export function effectiveStrata(state, well, strata) {
  const mdef = mudDef(well);
  const cake = cakeQuality(mdef);
  const mw = mudWeight(well);
  const collapseMult = stat(mdef, 'collapseMult', 1);
  const shale = stat(mdef, 'shaleStability', 1);
  return strata.map((s) => {
    let sd = s.stableDays;
    if (s.heaving) sd = mw >= 8.8 - 1e-9 ? ROTARY.heavingStableDaysMud * cake : 0;
    else {
      sd = sd * (1 + 5 * cake) / collapseMult;   // a filter cake holds clays & shales for weeks
      if (s.rock === 'shale') sd *= shale;
    }
    return sd === s.stableDays ? s : { ...s, stableDays: Math.round(sd * 10) / 10 };
  });
}

// ---- strings & mud bookkeeping ----------------------------------------------------------------
export function ensureMud(state, well) {
  if (well.mud?.defId) return mudDef(well);
  const d = defaultMud(state);
  if (!d) return null;
  well.mud = { ...(well.mud ?? {}), defId: d.id, ppg: clamp(well.mud?.ppg > 8.4 ? well.mud.ppg : 9.0, stat(d, 'ppgMin', 8.3), stat(d, 'ppgMax', 8.8)), auto: well.mud?.auto ?? false };
  return d;
}

// Mud for `ft` drilled: stock first, then bought; barite above 9.5 ppg; × lostMult on a loss day; mud man −15%.
export function chargeMud(state, well, ft, { lostMult = 1, mudMan = false } = {}) {
  const def = mudDef(well);
  if (!def || ft <= 0) return 0;
  const perFt = unitPrice(state, def);
  let fromStock = Math.min(ft, stockQty(state, def.id));
  if (fromStock > 0) takeStock(state, def.id, fromStock);
  const mw = mudWeight(well);
  let cost = ((ft - fromStock) * perFt + ft * baritePerFt(state, mw)) * (mudMan ? 0.85 : 1);
  if (lostMult > 1) cost += ft * perFt * (lostMult - 1);
  if (cost > 0) { spend(state, cost, 'consumables', false); well.cost += cost; }
  well.mudCost = (well.mudCost ?? 0) + cost;
  return cost;
}

// ---- shows & missed pay (M2_SPEC §1.4) ----------------------------------------------------------
// Tuned (bot): the spec's `MW − pore + 0.5` hid a third of all pay at an ordinary 0.3–0.5 ppg trip margin; counting only the
// actual overbalance keeps the lesson (1.5 ppg over → 60% missed) without punishing safe drilling.
export function missChance(mw, pore) {
  return clamp(MUD.overbalanceShowPenalty * Math.max(0, mw - pore), 0, 0.9);
}

export function revealPay(state, well, interval = null) {
  const out = [];
  const fdef = fieldDef(well.fieldId);
  const plot = state.fields[well.fieldId].plots[well.plot];
  for (const id of [...(well.bypassed ?? [])]) {
    const r = reservoirsAt(fdef, plot).find((x) => x.id === id);
    if (!r) continue;
    if (interval && (r.top + r.thick < interval[0] || r.top > interval[1])) continue;
    well.bypassed = well.bypassed.filter((x) => x !== id);
    if (!well.revealed.includes(id)) well.revealed.push(id);
    (well.foundPay ??= []).push(id);
    out.push(r);
  }
  if (out.length) notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'BYPASSED', pause: true, level: 'major',
    text: `${well.name}: the log shows oil-bearing sand the mud hid from the driller — ${out.map((r) => `${r.name ?? r.id} at ${Math.round(r.top)} ft`).join(', ')}. Recomplete up-hole to produce it.` });
  return out;
}

// ---- the day -----------------------------------------------------------------------------------
export function rotaryDrillDay(state, well) {
  const ctx = drillCtx(state, well);
  if (!ctx) { well.status = 'SUSPENDED'; return; }
  const { rig, rs } = ctx;
  const fdef = fieldDef(well.fieldId);
  const plot = state.fields[well.fieldId].plots[well.plot];
  well.openHoleDays++;

  if (!ctx.contractor) {
    if (!ctx.cf.hasDriller) return setProblem(state, well, 'NO_CREW', 'no driller on the rig — hire or assign one.', { pause: true });
    if (ctx.power <= 0) return setProblem(state, well, 'NO_ENGINE', 'this rig needs an engine installed.', { pause: true });
  }
  const m3g = m3DrillGate(state, well, ctx); // M3 ops: waiting on weather, deck supply, ice season, heave (null before 1946)
  if (m3g?.block) return;
  if (!ctx.contractor && waitingOnDriller(state, well, rig)) return; // hands-on drilling: you're on the brake or it waits
  if (!ensureMud(state, well)) return setProblem(state, well, 'NO_MUD', 'no drilling mud available.', { pause: true });
  if (!ctx.contractor && applyAutomation(state, well, rig)) return;
  if (!ctx.contractor && !ctx.pumpDef && !well._noPumpWarned) {
    well._noPumpWarned = true;
    notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'NO_PUMP', level: 'warn', text: `${well.name}: no mud pump on ${rig.name} — the bit is regrinding its own cuttings (ROP ×${ROTARY.noPumpRopMult}). Buy a slush pump.` });
  }
  const strata = effectiveStrata(state, well, strataAt(fdef, plot));
  const auto = rig?.auto ?? { case: true, tripBelowPct: 15, straighten: true, drillThroughPay: false };

  // unstable / heaving strata: case them off (M1 auto-case), then collapse rolls
  if (auto.case && (well.casing?.length ?? 0) < 4) {
    const loose = strata.find((s) => s.stableDays < 15 && well.depth >= s.bottom && well.shoe < s.bottom);
    if (loose && autoCase(state, well)) return;
    const aging = strata.find((s) => well.passed[s.idx] !== undefined && well.shoe < s.bottom && well.depth >= s.bottom && state.day - well.passed[s.idx] > s.stableDays * 0.8 && s.stableDays < 200);
    if (aging && autoCase(state, well)) return;
  }
  const wob = well.wob ?? 1;
  const rpm = well.rpm ?? 1;
  const water = mudDef(well) && cakeQuality(mudDef(well)) <= 0;
  for (const s of strata) {
    if (well.passed[s.idx] === undefined || well.shoe >= s.bottom || state._rush) continue;
    if (s.salt && water) {
      const open = state.day - well.passed[s.idx] + 1;
      if (chance(state, Math.min(0.5, 0.02 * open))) { notify(state, 'problem', { wellId: well.id, problem: 'WASHOUT', text: `${well.name}: fresh water is dissolving the salt — the hole washed out.`, level: 'warn' }); return collapse(state, well, s); }
      if (chance(state, Math.min(0.3, 0.01 * open))) return stickPipe(state, well, 'the string stuck in the washed-out salt');
    }
    const extra = state.day - well.passed[s.idx] - s.stableDays;
    if (extra <= 0) continue;
    const p = (s.hard <= 1 ? 0.03 + 0.01 * extra : 0.01 * (extra / 10)) * rotaryWobFactors(state, ctx, wob, s.hard).riskMult;
    if (chance(state, Math.min(0.5, p))) return collapse(state, well, s);
  }

  // bit
  if (!ctx.contractor) {
    const bit = ctx.bit;
    const trip = auto.tripBelowPct ?? 15;
    const bearing = bit?.bearing ?? 100;
    if (!bit || bit.condition <= trip + 0.5 || bearing <= trip + 0.5) {
      const spare = bestSpareBit(state, rig);
      if (spare) { startBitChange(state, well, rig, spare.uid, rotaryTripDays(rs, well.depth) * (m3g ? m3TripMult(state, rig, ctx.rigDef) : 1)); return; }
      if (!bit || bit.condition <= 0) return setProblem(state, well, 'NO_BIT', 'no usable bit — buy one in the shop.', { pause: true, event: 'bitWorn' });
      if (!bit.dullWarned) { bit.dullWarned = true; notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'DULL_BIT', level: 'warn', text: `${well.name}: the bit is worn past the trip-out point (${Math.round(Math.min(bit.condition, bearing))}%) and there's no spare on the rack — the crew is running it to destruction, slowly. Buy a bit.` }); }
    }
  }
  if (well.lateral && isLateralPhase(well)) return lateralDay(state, well, ctx); // M4-A: building the curve / drilling the lateral
  // depth limit
  const maxD = ctx.contractor ? well.contract.maxDepth : rs.maxDepth;
  const stopAt = well.stopAt && well.stopAt > well.depth + 1e-6 ? well.stopAt : Infinity;
  const depthLimit = Math.min(well.target, maxD, stopAt);
  if (well.depth >= depthLimit - 1e-6) {
    if (stopAt <= depthLimit + 1e-6 && stopAt < well.target - 1e-6) { well.stopAt = null; return applyAutomation(state, well, rig, { atCasingPoint: true }); }
    return reachTD(state, well);
  }
  if ((well.deviationDeg ?? 0) > 10) {
    if (auto.straighten !== false) { startStraighten(state, well, 'straighten'); return; }
    return setProblem(state, well, 'CROOKED', `hole is ${well.deviationDeg.toFixed(1)}° off vertical — straighten or sidetrack.`, { pause: true });
  }
  // pipe for today's hole (projected: ROP × 1 day at the current stratum, plus a joint)
  if (!ctx.contractor) {
    const s0 = stratumAt(strata, well.depth + 0.01);
    const want = Math.min(depthLimit, well.depth + rotaryRop(state, well, s0, ctx) + 30);
    if (!ensureString(state, well, want)) {
      const reach = (well.string?.joints ?? 0) * 30;
      if (reach <= well.depth + 1) return setProblem(state, well, 'OUT_OF_PIPE', `out of drill pipe at ${Math.round(well.depth)} ft — buy more joints.`, { pause: true });
    }
    ctx.pipeDef = well.string ? eqDef(well.string.defId) : null;
  }
  well.problem = null;

  // --- drill ---
  const pipeLimit = ctx.contractor ? Infinity : (well.string?.joints ?? 0) * 30;
  const limit = Math.min(depthLimit, pipeLimit);
  const resList = reservoirsAt(fdef, plot);
  const oldHand = traitCount(ctx.cf.crew, 'old_hand') ? 0.8 : 1;
  const mw = mudWeight(well);
  const tripPct = auto.tripBelowPct ?? 15;
  const bit = ctx.bit;
  const wearBudget = ctx.contractor ? Infinity : (bit.condition > tripPct + 0.5 ? bit.condition - tripPct : Infinity);
  let time = m3g?.time ?? 1, drilled = 0, wear = 0, event = null, rotTime = 0;
  const touch = touchToday(state, well), tRop = touchRopMult(touch), tWear = touchWearMult(touch); // driller's time
  well.minWob = Math.min(well.minWob ?? wob, wob);
  while (time > 1e-6 && well.depth < limit - 1e-6 && wear < wearBudget - 1e-9) {
    const s = stratumAt(strata, well.depth + 0.01);
    if (well.passed[s.idx] === undefined) {
      well.passed[s.idx] = state.day;
      if (s.idx >= 0) {
        if (s.gas) well.gasSeen[s.idx] = true;
        // a show: penetrating a permeable zone the mud doesn't hold (gas-cut mud, flow on connections)
        if ((s.gas || (s.pore ?? 8.6) > mw + 0.05) && !s.water && s.idx >= 0) showNotice(state, well, s, mw);
      }
    }
    const rop = rotaryRop(state, well, s, ctx) * tRop;
    if (rop <= 1e-6) break;
    let stop = Math.min(limit, s.bottom > well.depth + 1e-6 ? s.bottom : Infinity); // below the last stratum: it extends
    const nextRes = resList.find((r) => !well.revealed.includes(r.id) && !(well.bypassed ?? []).includes(r.id) && r.top < stop && r.top + r.thick > well.depth);
    if (nextRes && nextRes.top <= well.depth + 1e-6) {
      const res = state.fields[well.fieldId].reservoirs[nextRes.id];
      const pore = 8.4 + ((nextRes.pore ?? 9) - 8.4) * clamp(res?.pressure ?? 1, 0, 1);
      // a core over the interval (sim/m2/geophysics.js coreWell → well.cored) always brings the show up
      const cored = (well.cored ?? []).some(([t, b]) => t < nextRes.top + nextRes.thick && b > nextRes.top);
      if (!state._rush && !cored && res?.remaining > 0 && chance(state, missChance(mw, pore))) {
        (well.bypassed ??= []).push(nextRes.id);
        log(state, `${well.name}: drilled through ${Math.round(nextRes.top)}–${Math.round(nextRes.top + nextRes.thick)} ft — nothing in the cuttings.`, 'info', { wellId: well.id });
        continue;
      }
      event = { type: 'pay', r: nextRes };
      break;
    }
    if (nextRes) stop = nextRes.top + 0.5;
    const wearPerFt = ctx.contractor ? 0 : toothWearPerFt(state, ctx, s, wob, rpm, oldHand) * tWear;
    const can = Math.min(rop * time, wearPerFt > 0 && wearBudget !== Infinity ? (wearBudget - wear) / wearPerFt : Infinity);
    const ft = Math.max(0, Math.min(can, stop - well.depth));
    if (ft <= 1e-9 && !(nextRes && well.depth >= nextRes.top)) { if (can <= 1e-9) break; }
    well.depth += ft;
    drilled += ft;
    time -= ft / rop;
    rotTime += ft / rop;
    wear += ft * wearPerFt;
    well.deviationDeg = (well.deviationDeg ?? 0) + Math.max(0, wob - 1.2) * 0.4 * (ft / 100) * (s.dipping || s.dip ? 2 : 1)
      / Math.pow(rpm, ROTARY.deviationRpmExp) * stat(ctx.collarsDef, 'deviationMult', 1);
    if (nextRes && well.depth >= nextRes.top - 1e-6) {
      // re-enter the loop: the reservoir check above decides show vs. miss
      continue;
    }
  }
  well.depth = Math.round(well.depth * 100) / 100;
  if (well.reservoirId) {
    const r = resList.find((x) => x.id === well.reservoirId);
    if (r) well.payFt = clamp(well.depth - r.top, 0, r.thick);
  }

  // driller's touch: bouncing the bit in hard rock can twist the pipe off
  if (touch) { well.touch = null; if (touch.bounce > 0.3 && drilled > 0 && !ctx.contractor && chance(state, touch.bounce * 0.2)) { bit.condition = Math.max(0, bit.condition - 25); return twistOff(state, well); } }
  // --- wear, pipe, costs ---
  const mudMan = hasMudMan(state, rig);
  if (drilled > 0) {
    if (!ctx.contractor) {
      bit.condition = Math.max(0, bit.condition - wear);
      if (bit.bearing != null) bit.bearing = Math.max(0, bit.bearing - bearingWearPerDay(ctx, rpm) * Math.max(0.2, rotTime));
      if (well.string) well.string.fatigue = Math.min(100, well.string.fatigue + fatigueDay(well, ctx.pipeDef, { rpm, wob, rotFrac: Math.max(0.2, rotTime) }));
      rig.condition = Math.max(0, rig.condition - 0.02);
      for (const c of ctx.cf.crew) c.xp += drilled / 10;
    } else {
      const fee = drilled * well.contract.perFt;
      spend(state, fee, 'consumables', false);
      well.cost += fee;
      well.contract.billed += fee;
      well.contract.ft += drilled;
    }
    state.company.stats.ftDrilled += drilled;
    well.history.push([state.day, well.depth]);
    if (well.history.length > 400) well.history.splice(1, 1);
  }
  if (!ctx.contractor) chargeFuel(state, rig, well);

  // --- pressure: losses, kicks, sticking ---
  const pr = pressureRolls(state, well, { drilling: true, ftToday: drilled, mudMan });
  if (!ctx.contractor || drilled > 0) chargeMud(state, well, ctx.contractor ? 0 : Math.max(drilled, pr.loss ? 100 : 0), { lostMult: pr.loss ? MUD.lostCircMudCostMult : 1, mudMan });
  if (pr.loss) lostCirculation(state, well, pr.window);
  if (pr.kick) { onKick(state, well, pr.kick); return; }
  if (pr.stuck) return stickPipe(state, well, pr.stuck === 'diff' ? 'differentially stuck against a permeable sand (too much overbalance)' : 'stuck in the lost-circulation zone');

  // --- events ---
  if (event?.type === 'pay') return hitPay(state, well, rig, event.r, { rotary: true });
  if (state._rush) { if (well.depth >= well.target - 1e-6 || well.depth >= maxD - 1e-6) reachTD(state, well); return; }

  const h = stratumAt(strata, Math.max(0, well.depth - 0.5)).hard;
  const riskMult = rotaryWobFactors(state, ctx, wob, h).riskMult * (traitCount(ctx.cf.crew, 'careful') ? 0.7 : 1) * (1 + 0.4 * traitCount(ctx.cf.crew, 'reckless'));
  if (!ctx.contractor) {
    // lost cone on a dead bearing
    if (bit.bearing != null && bit.bearing <= 0 && chance(state, LOST_CONE_PER_DAY)) return startFishing(state, well, 'a cone locked up and broke off the bit');
    if (bit.condition <= 0 && chance(state, 0.02 * riskMult)) return startFishing(state, well, 'the worn-out bit broke off in the hole');
    if (drilled > 0 && rollTwistOff(state, well, ctx.pipeDef, { rpm, h, riskMult, collarsDef: ctx.collarsDef })) return twistOff(state, well);
  }
  if (m3g && drilled > 0) m3BitDamage(state, well, ctx, h, (p) => chance(state, p)); // M3 ops: PDC cutter loss above its rating
  const failed = collapseRoll(state, well);
  if (failed) return casingCollapse(state, well, failed);
  if (well.depth >= well.target - 1e-6 || well.depth >= maxD - 1e-6) reachTD(state, well);
}

function showNotice(state, well, s, mw) {
  const pore = s.pore ?? 8.6;
  learnPore(state, well, { key: `s${s.idx}` }, pore);
  if (pore <= mw + 1e-9) return;
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'GAS_SHOW', pause: true, level: 'warn', pore: Math.round(pore * 10) / 10, mw: Math.round(mw * 10) / 10,
    text: `${well.name}: gas-cut mud from the ${s.name ?? s.rock} at ${Math.round(s.top)} ft (~${pore.toFixed(1)} ppg vs. ${mw.toFixed(1)} in the hole). Weight up before it kicks!` });
}

function lostCirculation(state, well, w) {
  const l = (well.losses ??= { day: -999, n: 0 });
  l.n++;
  if (state.day - l.day > 10) notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'LOST_CIRC', pause: true, level: 'warn',
    text: `${well.name}: lost circulation — the mud (ECD ${w.ecd.toFixed(1)} ppg) is breaking down the formation (frac ${w.frac.toFixed(1)}). Mud costs ×${MUD.lostCircMudCostMult}; lighten up or case off the weak zone.` });
  l.day = state.day;
}

function casingCollapse(state, well, c) {
  well.casing = well.casing.filter((x) => x !== c);
  well.shoe = well.casing.reduce((a, x) => Math.max(a, x.bottom), 0);
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'CASING_COLLAPSE', pause: true, level: 'warn',
    text: `${well.name}: the ${eqDef(c.defId)?.name ?? 'casing'} collapsed under the pressure (rated ${stat(eqDef(c.defId), 'collapsePpg', 0)} ppg). Run heavier casing.` });
  stickPipe(state, well, 'crushed casing pinched the string');
}

// ---- twist-offs, stuck pipe, fishing (DESIGN §5.3) ------------------------------------------------
export function twistOff(state, well) {
  const twistDepth = well.depth * range(state, 0.3, 0.95);
  const joints = jointsFor(well.depth - twistDepth);
  bump(state, 'twistOffs');
  well.fish = { kind: 'twist', joints, twistDepth: Math.round(twistDepth) };
  startFishing(state, well, `the drill pipe twisted off at ${Math.round(twistDepth)} ft — ${joints} joints in the hole`);
}

export function stickPipe(state, well, why) {
  if (well.timerId) { cancelTimer(state, well.timerId); clearTask(state, well); }
  well.status = 'STUCK';
  well.stuck = { day: state.day, tries: 0, why, stuckDepth: Math.round(well.depth - range(state, 30, 300)) };
  well.fishingJobs = (well.fishingJobs ?? 0) + 1;
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'STUCK', pause: true, level: 'warn',
    text: `${well.name}: stuck pipe — ${why}. Working the pipe (40%/day); call a fishing contractor or sidetrack if it won't come free.` });
}

// Daily for STUCK wells: the crew works the pipe (40%/day, up to 5 days), then a fishing contractor (auto.fish).
export function stuckDay(state, well) {
  const st = well.stuck ?? (well.stuck = { day: state.day, tries: 0, stuckDepth: well.depth - 100 });
  const rig = wellRig(state, well);
  if (rig) chargeFuel(state, rig, well);
  if (st.tries < STUCK_WORK_DAYS) {
    st.tries++;
    if (chance(state, STUCK_WORK_CHANCE)) { freePipe(state, well, 'worked the pipe free'); return; }
    if (st.tries < STUCK_WORK_DAYS) return;
  }
  if (rig?.auto?.fish !== false && !st.contractor) fishImpl(state, well);
}

function freePipe(state, well, how) {
  well.status = 'DRILLING';
  well.stuck = null;
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'FISHED', text: `${well.name}: ${how} — back to drilling.` });
}

export function workPipeImpl(state, well) {
  if (well.status !== 'STUCK') return { ok: false, msg: 'The pipe is not stuck.' };
  const st = well.stuck;
  if (st.workedDay === state.day) return { ok: false, msg: 'The crew is already working the pipe today.' };
  st.workedDay = state.day;
  st.tries++;
  if (chance(state, STUCK_WORK_CHANCE)) { freePipe(state, well, 'jarred and worked the pipe free'); return { ok: true, msg: 'The pipe came free!' }; }
  return { ok: true, msg: `Still stuck after ${st.tries} attempt(s).` };
}

// Fishing contractor: $ + FISHING timer (3–10 d × jars), 80% success; on failure the string below the stuck point
// is lost and the hole is sidetracked.
export function fishImpl(state, well) {
  if (!['STUCK', 'FISHING'].includes(well.status) || well.status === 'FISHING') return { ok: false, msg: well.status === 'FISHING' ? 'Already fishing.' : 'Nothing to fish for.' };
  const cost = eraDollars(state, FISHING_CONTRACTOR, 1901);
  spend(state, cost, 'other', false);
  well.cost += cost;
  const st = well.stuck ?? {};
  st.contractor = true;
  well.status = 'FISHING';
  const days = Math.max(1, Math.round(int(state, 3, 10) * (effects(state).fishingTimeMult ?? 1)));
  setTask(state, well, 'FISHING', days, { contractor: true, why: st.why ?? 'stuck pipe', stuckDepth: st.stuckDepth });
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'FISHING', text: `${well.name}: fishing contractor rigged up (${fmt$(cost)}, ~${days} days).` });
  return { ok: true, msg: `Fishing contractor hired (${fmt$(cost)}), ~${days} days.` };
}

// Sidetrack: lose SIDETRACK_LOSS_FT (× directional drilling) of hole and the pipe below the stuck point.
export function sidetrackImpl(state, well) {
  if (!['STUCK', 'FISHING', 'DRILLING', 'PAUSED'].includes(well.status)) return { ok: false, msg: `Can't sidetrack while ${well.status}.` };
  const loss = Math.round(SIDETRACK_LOSS_FT * (effects(state).sidetrackLossMult ?? 1));
  const stuckDepth = well.stuck?.stuckDepth ?? well.fish?.twistDepth ?? well.depth - 100;
  const lost = loseJoints(state, well, jointsFor(well.depth - stuckDepth));
  if (well.timerId) { cancelTimer(state, well.timerId); clearTask(state, well); }
  well.stuck = null;
  well.fish = null;
  const rig = wellRig(state, well);
  if (rig && state.items[rig.bitUid] && lost > 0) { delete state.items[rig.bitUid]; rig.bitUid = null; well.bitUid = null; }
  well.status = 'TRIPPING';
  // kept after the timer so the 2D/3D views can draw the junked hole and the new path
  well.sidetrack = { kickoff: Math.round(Math.max(well.shoe ?? 0, well.depth - loss)), oldDepth: Math.round(well.depth), day: state.day };
  setTask(state, well, 'STRAIGHTEN', int(state, 2, 4), { method: 'sidetrack', lossFt: loss });
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'SIDETRACK', level: 'warn', text: `${well.name}: sidetracking around the junk — ${loss} ft of hole and ${lost} joints of pipe lost.` });
  if (rig && !rig.bitUid) { const spare = bestSpareBit(state, rig); if (spare) { spare.loc = { type: 'rig', id: rig.uid }; rig.bitUid = spare.uid; well.bitUid = spare.uid; } }
  return { ok: true, msg: `Sidetracking: ${loss} ft and ${lost} joints lost.` };
}

// After a twist-off is fished out the crew lays down the cracked joint and its worst neighbours.
function layDownCracked(well) {
  if (well.fish?.kind !== 'twist' || !well.string) return;
  well.string.joints = Math.max(0, well.string.joints - 2);
  well.string.fatigue = Math.max(0, well.string.fatigue - 15);
}

// FISHING timer done on a rotary well. Returns true when handled here.
export function onRotaryFishDone(state, well, t, rushed) {
  const rig = wellRig(state, well);
  if (t.data?.contractor) {
    if (rushed || chance(state, 0.8)) { layDownCracked(well); well.stuck = null; well.fish = null; well.status = 'DRILLING'; notify(state, 'problem', { wellId: well.id, problem: 'FISHED', text: `${well.name}: the fishing contractor got the string out — back to drilling.` }); return true; }
    sidetrackImpl(state, well);
    return true;
  }
  const skill = rig ? crewFactors(state, rig).crew.find((c) => c.role === 'driller')?.skill ?? 30 : 50;
  if (rushed || chance(state, 0.7 + skill / 500)) {
    notify(state, 'problem', { wellId: well.id, problem: 'FISHED', text: `${well.name}: fish recovered — back to drilling.` });
    layDownCracked(well);
    well.fishTries = 0; well.fish = null; well.status = 'DRILLING';
    return true;
  }
  if ((well.fishTries ?? 0) < 3) { startFishing(state, well, 'the overshot slipped off the fish'); return true; }
  well.fishTries = 0;
  sidetrackImpl(state, well);
  return true;
}

// ---- open hole while not drilling (paused, tripping: swab, waiting at TD) -----------------------------
export function rotaryOpenHoleDay(state, well) {
  if (!well.mud?.defId || well.depth <= 0) return;
  const rig = wellRig(state, well);
  const tripping = well.status === 'TRIPPING';
  const pr = pressureRolls(state, well, { tripping, drilling: false, mudMan: hasMudMan(state, rig) });
  if (pr.loss) { chargeMud(state, well, 100, { lostMult: MUD.lostCircMudCostMult }); lostCirculation(state, well, pr.window); }
  if (pr.kick) onKick(state, well, pr.kick);
}

// ---- contract drilling (M2_SPEC §1.5) ----------------------------------------------------------------
export function contractDrillImpl(state, fieldId, plotIdx, targetFt) {
  const f = state.fields[fieldId];
  const plot = f?.plots[plotIdx];
  if (!plot) return { ok: false, msg: 'No such plot.' };
  if (yf(state) < 1901) return { ok: false, msg: 'There are no rotary drilling contractors before 1901.' };
  if (!plot.owned) return { ok: false, msg: 'Lease the plot first.' };
  if (plot.wellId) return { ok: false, msg: 'There is already a well on that plot.' };
  if ((plot.waterDepthFt ?? 0) > 0) return { ok: false, msg: 'The land contractor can’t drill offshore: bring your own offshore rig, or set a platform first.' }; // the well would have no host to produce into
  const y = yf(state);
  const maxDepth = contractMaxDepth(y);
  const target = clamp(Math.round(targetFt || maxDepth), 50, maxDepth);
  const perFt = eraDollars(state, contractPerFt(y), CONTRACT_DRILLING.baseYear);
  const rigDef = contractorRigDef(state);
  if (!rigDef) return { ok: false, msg: 'No contractor rig available.' };
  if (state.company.cash < perFt * Math.min(target, 300)) return { ok: false, msg: `The contractor wants to see ${fmt$(perFt * 300)} up front.` };
  const pseudo = { uid: null, defId: rigDef.id, crewIds: [], bitUid: null };
  const well = createWell(state, pseudo, fieldId, plotIdx, target);
  well.rigId = null;
  well.rigType = 'rotary';
  well.target = target;
  well.contract = { perFt, maxDepth, startDay: state.day, readyDay: state.day + CONTRACT_DRILLING.mobilizationDays, billed: 0, ft: 0, rigDefId: rigDef.id,
    bitDefId: bestDef(state, 'bit', (d) => d.rigTypes?.includes('rotary'))?.id ?? null };
  well.poreGuess = Math.round(range(state, -0.5, 0.5) * 100) / 100;
  ensureMud(state, well);
  well.status = 'RIGGING_UP';
  setTask(state, well, 'CONTRACT_MOB', CONTRACT_DRILLING.mobilizationDays);
  return { ok: true, msg: `Drilling contractor hired: ${fmt$(perFt)}/ft to ${target} ft, moving in within ${CONTRACT_DRILLING.mobilizationDays} days.` };
}

// ---- recompletion up-hole to a bypassed (now logged) pay (M2_SPEC §1.4) ---------------------------------
export function recompleteImpl(state, well, resId) {
  if (well.ownerOperatorId) return { ok: false, msg: 'Contract well.' };
  if (!['DRY', 'AT_TD', 'SUSPENDED', 'PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(well.status)) return { ok: false, msg: `Can't recomplete while ${well.status}.` };
  const r = reservoirsAt(fieldDef(well.fieldId), state.fields[well.fieldId].plots[well.plot]).find((x) => x.id === resId);
  if (!r || !well.revealed.includes(resId)) return { ok: false, msg: 'Log or core the well first: nobody has seen pay there.' };
  if (r.top > well.depth) return { ok: false, msg: 'That sand is below the bottom of the hole.' };
  if (well.reservoirId === resId && well.prod) return { ok: false, msg: 'Already producing from that sand.' };
  const cost = eraDollars(state, 150, 1901);
  spend(state, cost, 'other', false);
  well.cost += cost;
  if (well.prod) { well.prod.shutIn = true; well.prod.rate = 0; }
  well.reservoirId = resId;
  well.payTop = r.top;
  well.payFt = r.thick;
  well.strikeDay ??= state.day;
  well.status = 'COMPLETING';
  well.bypassed = (well.bypassed ?? []).filter((x) => x !== resId);
  const prev = well.prod;
  well.prod = null;
  if (prev) well._prevCum = (well._prevCum ?? 0) + (prev.cum ?? 0);
  state.company.stats.strikes++;
  setTask(state, well, 'RECOMPLETE', 2);
  return { ok: true, msg: `Perforating the ${r.name ?? r.id} at ${Math.round(r.top)} ft (2 days, ${fmt$(cost)}).` };
}

// ---- projections for the UI (RPM curve, cost per foot incl. bearing life) --------------------------------
export function rotaryProjection(state, well, s, { wob, rpm }, dailyCost) {
  const ctx = drillCtx(state, well);
  if (!ctx?.bit) return { wob, rpm, rop: 0, costPerFt: null, risk: 0, bitDays: 0 };
  const rop = rotaryRop(state, well, s, ctx, { wob, rpm });
  const tooth = toothWearPerFt(state, ctx, s, wob, rpm);
  const bearingPerDay = bearingWearPerDay(ctx, rpm);
  const trip = ctx.rig?.auto?.tripBelowPct ?? 15;
  const usable = Math.max(1, 100 - trip);
  const ftByTeeth = usable / Math.max(tooth, 1e-6);
  const ftByBearing = bearingPerDay > 0 && rop > 0 ? (usable / bearingPerDay) * rop : Infinity;
  const ftPerRun = Math.min(ftByTeeth, ftByBearing);
  const drillDays = rop > 0 ? ftPerRun / rop : Infinity;
  const tripDays = rotaryTripDays(ctx.rs, well.depth);
  const bitCost = ctx.bitDef ? unitPrice(state, ctx.bitDef) : 0;
  const h = Math.max(1, s.hard);
  const risk = rotaryWobFactors(state, ctx, wob, h).riskMult * (1 + ROTARY.rpmRiskSlope * Math.max(0, rpm - ROTARY.rpmRiskKnee) * (h >= 6 ? 1.5 : 1));
  const mdef = mudDef(well);
  const mudFt = mdef ? unitPrice(state, mdef) + baritePerFt(state, mudWeight(well)) : 0;
  const costPerFt = rop > 0 && Number.isFinite(drillDays) ? (bitCost + dailyCost * (drillDays + tripDays)) / ftPerRun + mudFt : null;
  const condNow = Math.min(ctx.bit.condition - trip, (ctx.bit.bearing ?? 100) - trip);
  return { wob, rpm, rop, costPerFt, risk, bitDays: rop > 0 ? Math.max(0, condNow) / Math.max(tooth * rop, bearingPerDay, 1e-6) : Infinity };
}

export function rpmCurve(state, well, s, dailyCost) {
  const out = [];
  for (let r = RPM_MIN; r <= RPM_MAX + 1e-9; r += 0.1) {
    const p = rotaryProjection(state, well, s, { wob: well.wob ?? 1, rpm: Math.round(r * 10) / 10 }, dailyCost);
    out.push({ rpm: p.rpm, rop: p.rop, costPerFt: p.costPerFt, risk: p.risk });
  }
  return out;
}

// Best (wob, rpm) for lowest cost/ft — the toolpusher's optimizer (DESIGN §14).
export function optimizeSliders(state, well, s, dailyCost) {
  let best = null;
  for (let w = 0.6; w <= 1.4 + 1e-9; w += 0.1) {
    for (let r = 0.7; r <= 1.4 + 1e-9; r += 0.1) {
      const p = rotaryProjection(state, well, s, { wob: Math.round(w * 10) / 10, rpm: Math.round(r * 10) / 10 }, dailyCost);
      if (p.costPerFt == null) continue;
      const score = p.costPerFt * (1 + 0.15 * Math.max(0, p.risk - 1));
      if (!best || score < best.score) best = { ...p, score };
    }
  }
  return best;
}

export { WOB_MIN, WOB_MAX, isAvailable, findDef };
